((window) => {
    const removeEmptyFromObject = (obj) =>
        Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null));

    const defaults = {};

    const getUniqueIdentifier = () => (Math.random() + 1).toString(36).substring(7);

    const getSettings = (carousel) => {
        if (!carousel.dataset.uniqueIdentifier) {
            carousel.setAttribute('data-unique-identifier', getUniqueIdentifier());
        }

        const options = {
            uniqueIdentifier: carousel.dataset.uniqueIdentifier,
            breakpoint: parseInt(carousel.dataset.paginatedBreakpoint) || 0,
            mobileSlides: carousel.dataset.paginatedMobile,
            desktopSlides: carousel.dataset.paginatedDesktop,
            active: carousel.dataset.paginatedActive,
            gap: parseFloat(carousel.dataset.paginatedGap) || 0,
            pageClass: carousel.dataset.paginationPageClass,
            pageActive: carousel.dataset.paginationPageActive,
            pageWidth: carousel.dataset.paginationPageWidth,
            activePage: parseInt(carousel.dataset.paginationActivePage) || 1,
            paginatedAnimate: parseInt(carousel.dataset.paginatedAnimate) || false
        };

        return {
            ...defaults,
            ...removeEmptyFromObject(options),
        };
    };

    const stopAnimation = (id) => {
        if (window.paginatedSlides && window.paginatedSlides[id]) {
            clearInterval(window.paginatedSlides[id]);
        }
    };

    const createPageButton = (pagination, settings, pageNumber) => {
        const pageElement = document.createElement("button");
        const wrapperElement = document.createElement("li");

        pageElement.setAttribute(
            "data-behaviour",
            "update-paginated-slides-pagination"
        );
        pageElement.setAttribute("data-pagination-page", pageNumber);
        pageElement.innerText = pageNumber;
        pageElement.classList.add(settings.pageClass);

        if (pageNumber === settings.activePage) {
            pageElement.classList.add(settings.pageActive);
        }

        wrapperElement.appendChild(pageElement);
        pagination.appendChild(wrapperElement);
    };

    const createDots = (pagination) => {
        const dotsElement = document.createElement("span");
        const wrapperElement = document.createElement("li");

        dotsElement.innerText = "...";
        dotsElement.classList.add("pagination-dots");

        wrapperElement.appendChild(dotsElement);
        pagination.appendChild(wrapperElement);
    };

    const generatePagination = (carousel, settings, pages) => {
        const pagination = carousel.querySelector("[data-paginated-pagination]");

        if (!pagination) return;

        pagination.innerHTML = "";

        if (pages <= 1) return;

        if (pages <= 4) {
            for (let i = 1; i <= pages; i++) {
                createPageButton(pagination, settings, i);
            }
            return;
        }

        const pagesToShow = new Set();

        // Always show first and last
        pagesToShow.add(1);
        pagesToShow.add(pages);

        // Beginning
        if (settings.activePage <= 2) {
            pagesToShow.add(2);
            pagesToShow.add(3);
        }
        // End
        else if (settings.activePage >= pages - 1) {
            pagesToShow.add(pages - 2);
            pagesToShow.add(pages - 1);
        }
        // Middle
        else {
            pagesToShow.add(settings.activePage - 1);
            pagesToShow.add(settings.activePage);
            pagesToShow.add(settings.activePage + 1);
        }

        const sortedPages = [...pagesToShow]
            .filter((page) => page > 0 && page <= pages)
            .sort((a, b) => a - b);

        sortedPages.forEach((page, index) => {
            if (index > 0 && page - sortedPages[index - 1] > 1) {
                createDots(pagination);
            }

            createPageButton(pagination, settings, page);
        });
    };

    const getSlideWidth = (slidesCount, carousel, settings) =>
        slidesCount == 1
            ? carousel.getBoundingClientRect().width
            : (carousel.getBoundingClientRect().width -
                settings.gap * (slidesCount - 1)) / slidesCount;

    const getPageWidth = (slideWidth, slidesCount, settings) => {
        let pageWidth;

        switch (true) {
            case slidesCount === Math.floor(slidesCount) && slidesCount !== 1:
                pageWidth = parseFloat(settings.pageWidth) + parseFloat(settings.gap);
                break;

            case slidesCount === 1:
                pageWidth = Math.floor(slidesCount) * (
                    parseFloat(slideWidth) + (1 * parseFloat(settings.gap))
                );
                break;

            default:
                pageWidth = (parseFloat(settings.pageWidth) / slidesCount) +
                    (parseFloat(settings.gap) / slidesCount);
        }

        return pageWidth;
    };

    const goToPage = (page, carousel) => {
        const settings = getSettings(carousel);
        const wrapper = carousel.querySelector("[data-paginated-slides]");
        const isDesktop = window.innerWidth > settings.breakpoint;
        const slidesCount = parseFloat(
            isDesktop ? settings.desktopSlides : settings.mobileSlides
        );

        const slideWidth = getSlideWidth(slidesCount, carousel, settings);
        const pageWidth = getPageWidth(slideWidth, slidesCount, settings);

        wrapper.style.transform = `translateX(-${(page - 1) * pageWidth}px)`;
        carousel.setAttribute("data-pagination-active-page", page);

        const slides = [...wrapper.querySelectorAll(":scope > *")];
        const pages = Math.ceil(slides.length / slidesCount);

        if (settings.pageClass) {
            generatePagination(
                carousel,
                {
                    ...settings,
                    activePage: parseInt(page)
                },
                pages
            );
        }
    };

    const generateAnimation = (carousel, settings) => {
        if (!window.paginatedSlides) window.paginatedSlides = {};

        window.paginatedSlides[settings.uniqueIdentifier] = setInterval(() => {
            const updatedSettings = getSettings(carousel);
            const slides = [...carousel.querySelectorAll('[data-paginated-slides] > *')];
            const currentPage = updatedSettings.activePage;
            const isDesktop = window.innerWidth > updatedSettings.breakpoint;
            const slidesCount = parseFloat(
                isDesktop ? updatedSettings.desktopSlides : updatedSettings.mobileSlides
            );

            const totalPages = Math.ceil(slides.length / slidesCount);
            const nextPage = currentPage < totalPages ? currentPage + 1 : 1;

            if (!carousel.querySelector('[data-paginated-slides]').matches(':hover')) {
                goToPage(nextPage, carousel);
            }
        }, settings.paginatedAnimate);
    };

    const carouselSetup = () => {
        [...document.querySelectorAll('[data-behaviour="paginated-slides"]')].map((carousel) => {
            const settings = getSettings(carousel);
            const wrapper = carousel.querySelector("[data-paginated-slides]");

            if (!wrapper) return;

            const slides = [...wrapper.querySelectorAll(":scope > *")];
            carousel.style.maxWidth = 'unset';

            if (settings.active) {
                carousel.classList.remove(settings.active);
            }

            wrapper.style.width = "unset";

            const isDesktop = window.innerWidth > settings.breakpoint;
            const slidesCount = parseFloat(
                isDesktop ? settings.desktopSlides : settings.mobileSlides
            );

            if (!slidesCount) {
                return generatePagination(carousel, settings, 1);
            }

            const pages = Math.ceil(slides.length / slidesCount);

            settings.pageWidth = wrapper.getBoundingClientRect().width;
            carousel.setAttribute("data-pagination-page-width", settings.pageWidth);
            carousel.style.maxWidth = `${settings.pageWidth}px`;

            const slideWidth =
                slidesCount === 1
                    ? settings.pageWidth
                    : (settings.pageWidth - settings.gap * (slidesCount - 1)) / slidesCount;

            const slidesWidth =
                slideWidth * slides.length + (settings.gap * (slides.length - 1));

            wrapper.style.width = `${slidesWidth}px`;

            if (settings.active) {
                carousel.classList.add(settings.active);
            }

            wrapper.style.transform = `translateX(-${
                (settings.activePage - 1) * settings.pageWidth +
                (settings.activePage - 1) * settings.gap
            }px)`;

            if (settings.paginatedAnimate) {
                stopAnimation(settings.uniqueIdentifier);
                generateAnimation(carousel, settings);
            }

            if (settings.pageClass) {
                generatePagination(carousel, settings, pages);
            }
        });
    };

    document.addEventListener("DOMContentLoaded", carouselSetup);
    window.addEventListener("resize", () => setTimeout(carouselSetup, 300));

    document.addEventListener("click", (e) => {
        if (!e.target.matches('[data-behaviour="update-paginated-slides-pagination"]')) return;

        const carousel = e.target.closest('[data-behaviour="paginated-slides"]');

        if (!carousel) return;

        const page = parseInt(e.target.dataset.paginationPage);
        goToPage(page, carousel);
    });
})(window);